"use client";

import React, { useEffect, useState } from "react";
import { useTable, usePagination } from "react-table";
import { CSVLink } from "react-csv";
import DownloadIcon from "@mui/icons-material/Download";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { EmptyState, LoadingState } from "../UI/States";

const WebleadReferrals = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pageSizeSelected, updatePageSize] = useState(20);

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        const response = await fetch("/api/leads/v2/getLeadReferrals");
        if (!response.ok) {
          throw new Error("Failed to fetch referrals");
        }
        const data = await response.json();

        const formattedData = data
          .map((user) => ({
            _id: user._id,
            entryDate: user.webleadEntry?.entryDate
              ? new Date(user.webleadEntry.entryDate).toLocaleDateString(
                  "en-US"
                )
              : "N/A",
            customerName: user.webleadEntry?.customerName || "N/A",
            customerEmail: user.webleadEntry?.customerEmail || "N/A",
            customerPhone: user.webleadEntry?.customerPhone || "N/A",
            hearAbout:
              (user.webleadEntry?.hearAbout === "Other" &&
                user.webleadEntry?.hearOther === "") ||
              user.webleadEntry?.hearAbout === ""
                ? null
                : user.webleadEntry?.hearAbout === "Other"
                ? user.webleadEntry?.hearOther
                : user.webleadEntry?.hearAbout,
            other: user.webleadEntry?.hearAbout === "Other" ? 1 : 0,
          }))
          .filter((lead) => lead.hearAbout !== null);

        setLeads(formattedData.reverse());
        setLoading(false);
      } catch (error) {
        console.error("Error fetching referrals:", error);
        setErrorMessage("Unable to load referrals. Please try again.");
        setLoading(false);
      }
    };

    fetchReferrals();
  }, []);

  const filteredLeads = React.useMemo(() => {
    return leads.filter((lead) => {
      const entryDate = new Date(lead.entryDate);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      return (!start || entryDate >= start) && (!end || entryDate <= end);
    });
  }, [leads, startDate, endDate]);

  const columns = React.useMemo(
    () => [
      {
        Header: "Entry Date",
        accessor: "entryDate",
      },
      {
        Header: "Name",
        accessor: "customerName",
      },
      {
        Header: "Email",
        accessor: "customerEmail",
      },
      {
        Header: "Phone",
        accessor: "customerPhone",
      },
      {
        Header: "Referral",
        accessor: "hearAbout",
        Cell: ({ value }) => <div className="w-40 break-words">{value}</div>,
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex, pageSize },
    setPageSize, // Destructure the setPageSize from the useTable hook
  } = useTable(
    {
      columns,
      data: filteredLeads,
      initialState: { pageIndex: 0, pageSize: pageSizeSelected }, // Set default page size
    },
    usePagination
  );

  const csvHeaders = [
    { label: "Entry Date", key: "entryDate" },
    { label: "Name", key: "customerName" },
    { label: "Email", key: "customerEmail" },
    { label: "Phone", key: "customerPhone" },
    { label: "Referral", key: "hearAbout" },
    { label: "Other", key: "other" },
  ];

  const controlClass = "app-input rounded-xl border px-3 py-2 text-sm";

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold tabular-nums">
          {filteredLeads.length} referral leads
        </h2>
        <p className="app-muted text-sm">
          Filter by date, export to CSV, and browse how people heard about us.
        </p>
      </div>

      <div className="app-surface flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <fieldset className="flex flex-col gap-1">
            <legend className="app-muted mb-1 text-xs font-medium">
              Date range
            </legend>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={controlClass}
                aria-label="Start date"
              />
              <span className="app-muted text-sm" aria-hidden="true">
                –
              </span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={controlClass}
                aria-label="End date"
              />
            </div>
          </fieldset>

          <label className="flex flex-col gap-1">
            <span className="app-muted text-xs font-medium">Rows per page</span>
            <select
              value={pageSizeSelected}
              onChange={(e) => {
                updatePageSize(Number(e.target.value));
                setPageSize(Number(e.target.value));
              }}
              className={controlClass}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>

        <CSVLink
          data={filteredLeads}
          headers={csvHeaders}
          filename={`weblead_referrals_${new Date().toISOString().split("T")[0]}.csv`}
          className="app-btn app-btn-secondary self-start sm:self-auto"
        >
          <DownloadIcon sx={{ fontSize: 18 }} />
          Export CSV
        </CSVLink>
      </div>

      <div className="flex w-full flex-col">
        {loading && <LoadingState label="Loading..." />}
        {errorMessage && (
          <div
            role="alert"
            className="mb-2 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
          >
            {errorMessage}
          </div>
        )}
        {!loading && filteredLeads.length === 0 && (
          <div className="app-surface rounded-xl border">
            <EmptyState
              compact
              icon={CampaignOutlinedIcon}
              title="No leads to display."
              description="Try widening the date range."
            />
          </div>
        )}
        {!loading && filteredLeads.length > 0 && (
          <div className="app-surface overflow-hidden rounded-xl border">
            <div className="overflow-x-auto">
              <table {...getTableProps()} className="app-table">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="break-words !align-top"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between gap-3 border-t app-divider px-4 py-3">
              <button
                type="button"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="app-btn app-btn-secondary app-btn-sm"
              >
                Previous
              </button>
              <div className="app-muted text-center text-sm tabular-nums">
                Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
              </div>
              <button
                type="button"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="app-btn app-btn-secondary app-btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebleadReferrals;
